import { Accordion, AccordionDetails, AccordionSummary, AutocompleteRenderInputParams, InputAdornment } from "@mui/material";
import { styled } from "@mui/material/styles";
import { grey } from "designSystem/styles/color";
import { DatePicker, FlexColumn, FlexRowCenter, FormControlLabel, OverflowText, SingleSelect, Text, TextField } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { OverflowTextProps } from "designSystem/components/text/OverflowText";
import OverflowTextField from "designSystem/components/textfield/OverflowTextField";
import { ComponentItem } from "designSystem/types/component-item-types";
import types from "csi/types";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, HTMLAttributes, ReactNode } from "react";
import { GENERIC_UNNAMED_STAGE, PROOF_DECISION_STATUS } from "../ProofService";
import { GreyLabelText } from "./ProofingStage";
import { ProofingStageNumberChip } from "./ProofingStageNumberChip";
import { useProofingStore } from "./ProofingStore";

const StyledSingleSelectForStage = styled(SingleSelect)(({ theme }) => ({
	width: 186,
}));

interface ProofingStageAccordionProps {
	isEditOperation?: boolean;
	stageIndex: number;
}

export const ProofingStageAccordion: FunctionComponent<ProofingStageAccordionProps> = observer((props) => {
	const store = useProofingStore();
	const {
		stageTriggerTypeOptions,
		isTemplate,
		stages,
		stagesById,
	} = store;
	const { isEditOperation, stageIndex } = props;
	const stage = stages[stageIndex - 1];
	const {
		allowedStagesExceptThisStageAsComponentItems,
		choseSpecificDueDate,
		commentsTypeValue,
		dueDate,
		finalStageDecisionCalculationValue,
		isStageLockedAndStarted,
		isStageTriggerStartsWhen,
		lockStageValue,
		noPrimaryDecisionMaker,
		parentStage,
		primaryDecisionMakerOptions,
		primaryDecisionMakerPreferredGroup,
		primaryDecisionMakerValue,
		selectedThreshold,
		setPrimaryDecisionMaker,
		stageDecisionStatus,
		stageTriggerTypeValue,
	} = stage;
	const loc = useLocale();
	const showDateError = stage.dueDateError?.length > 0 && (!isEditOperation || !stage.areNewAndOldDueDatesSame);
	const isStageTriggerEditDisabled = isEditOperation && stageDecisionStatus && stageDecisionStatus !== PROOF_DECISION_STATUS.NOT_STARTED;
	const parentStageNameOrUnnamed = parentStage?.stageName || loc.resource(GENERIC_UNNAMED_STAGE);
	const textForDueDate = choseSpecificDueDate ? (types.formatTime(dueDate?.getTime(), types.FORMAT_DATE) || loc.resource("None")) : loc.getComponentItemName(selectedThreshold);

	// render stage number chip in text field
	function renderInputForDependentStage(params: AutocompleteRenderInputParams): ReactNode {
		const { parentStage } = stage;
		return (
			<OverflowTextField
				{...params}
				InputProps={{
					...params.InputProps,
					startAdornment: (
						<InputAdornment position="start">
							<ProofingStageNumberChip stageIndex={parentStage.stageNumber} />
						</InputAdornment>
					),
				}}
				inputProps={{
					...params.inputProps,
					value: parentStageNameOrUnnamed,
				}}
			/>
		);
	}

	// render stage number chip in popper
	function renderOptionForDependentStage<T extends ComponentItem>(props: HTMLAttributes<HTMLLIElement>, option: T): ReactNode {
		const {
			id,
			name,
		} = option;
		const overflowTextProps: OverflowTextProps = { ml: 1 };
		if (name) {
			overflowTextProps.text = name;
		} else {
			overflowTextProps.sx = { fontStyle: "italic" };
			overflowTextProps.res = GENERIC_UNNAMED_STAGE;
		}
		return (
			<li
				{...props}
				key={id}
			>
				<FlexRowCenter>
					<ProofingStageNumberChip stageIndex={stagesById[id].stageNumber} />
					<OverflowText {...overflowTextProps} />
				</FlexRowCenter>
			</li>
		);
	}

	return (
		<FlexRowCenter m={3}>
			<Accordion
				disableGutters
				expanded={stage.expand}
				onChange={(event: React.SyntheticEvent, expanded: boolean) => stage.setExpand(event, expanded)}
				square
				sx={{
					backgroundColor: grey[50],
					border: "none",
					width: "100%",
					paddingLeft: "54px",
				}}
			>
				<AccordionSummary>
					<FlexRowCenter
						flexGrow={1}
						gap={2}
					>
						<FlexRowCenter gap={1}>
							<GreyLabelText
								bold
								res="ProofStageStart"
							/>
							{!isStageTriggerStartsWhen
								?
								<Text res="ProofStageStartImmediately" />
								: (
									<FlexRowCenter gap={1}>
										<Text res="ProofStageStartWhen" />
										<OverflowText
											bold
											maxWidth={100}
											text={parentStageNameOrUnnamed}
										/>
										<Text
											res={stage.stageTriggerType}
											sx={{ textTransform: "lowercase" }}
										/>
									</FlexRowCenter>
								)}
						</FlexRowCenter>
						<FlexRowCenter gap={1}>
							<GreyLabelText
								bold
								noWrap
								res="DueDateField"
							/>
							<Text text={textForDueDate} />
						</FlexRowCenter>
					</FlexRowCenter>
				</AccordionSummary>
				<AccordionDetails>
					<FlexColumn
						borderTop={`1px solid ${grey[400]}`}
						flexGrow={1}
						gap={1}
					>
						<FlexRowCenter
							flexGrow={1}
							mt={3}
						>
							<GreyLabelText
								bold
								mr={1}
								res="ProofStageStart"
							/>
						</FlexRowCenter>
						<FlexRowCenter
							flexGrow={1}
							gap={2}
						>
							<StyledSingleSelectForStage
								disableClearable
								disabled={isStageTriggerEditDisabled}
								onChange={(e, value) => stage.setStageTrigger(value)}
								options={store.stageTriggerOptions}
								value={stage.stageTrigger}
							/>
							{isStageTriggerStartsWhen && (
								<>
									<StyledSingleSelectForStage
										disableClearable
										disabled={isStageTriggerEditDisabled}
										onChange={(e, value) => stage.setParentStage(value)}
										options={allowedStagesExceptThisStageAsComponentItems}
										renderInput={renderInputForDependentStage}
										renderOption={renderOptionForDependentStage}
									/>
									<StyledSingleSelectForStage
										disableClearable
										disabled={isStageTriggerEditDisabled}
										onChange={(e, value) => stage.setStageTriggerType(value)}
										options={stageTriggerTypeOptions}
										value={stageTriggerTypeValue}
									/>
								</>
							)}
						</FlexRowCenter>
						<FlexRowCenter
							flexGrow={1}
							mt={3}
						>
							<GreyLabelText
								bold
								mr={1}
								res="DueDateField"
							/>
						</FlexRowCenter>
						<FlexRowCenter flexGrow={1}>
							<StyledSingleSelectForStage
								disableClearable
								disabled={isStageLockedAndStarted}
								onChange={(e, value) => stage.setShowDueDate(value)}
								options={stage.dueDateOptions}
								value={stage.selectedThreshold}
							/>
							{stage.showDueDate && (
								<FormControlLabel
									control={(
										<DatePicker
											disablePast
											disabled={isStageLockedAndStarted}
											inputSx={{ width: 108 }}
											invalid={!stage.isDueDateValid}
											noRedBorderOnError={!showDateError}
											onChange={(value) => stage.setDueDate(value)}
											onError={(error, value) => stage.setDueDateError(error?.toString())}
											revertToPreviousValue
											value={stage.dueDate}
										/>
									)}
									labelPlacement="start"
								/>
							)}
						</FlexRowCenter>
						<FlexRowCenter
							flexGrow={1}
							gap={2}
						>
							<GreyLabelText
								bold
								mr={1}
								res="LockStage"
							/>
						</FlexRowCenter>
						<FlexRowCenter
							flexGrow={1}
							gap={2}
						>
							<StyledSingleSelectForStage
								disableClearable
								disabled={isStageLockedAndStarted}
								onChange={stage.setLockStageType}
								options={store.lockStageOptions}
								value={lockStageValue}
							/>
						</FlexRowCenter>
						<FlexRowCenter
							flexGrow={1}
							gap={2}
						>
							<GreyLabelText
								bold
								mr={1}
								res="StageDecisionCalculation"
							/>
						</FlexRowCenter>
						<FlexRowCenter
							flexGrow={1}
							gap={2}
						>
							<StyledSingleSelectForStage
								disabled={isStageLockedAndStarted}
								onChange={stage.setFinalStageDecisionCalculationType}
								options={stage.finalStageDecisionCalculationOptions}
								required
								value={finalStageDecisionCalculationValue}
							/>
							{stage.isStageDecisionCalculationDecisionMadeBy && (
								<StyledSingleSelectForStage
									disabled={isStageLockedAndStarted}
									grouped={isTemplate}
									helperTextRes={noPrimaryDecisionMaker ? "NoPrimaryDecisionMakerMessage" : ""}
									invalid={noPrimaryDecisionMaker}
									onChange={setPrimaryDecisionMaker}
									options={primaryDecisionMakerOptions}
									placeHolderRes="ApproverPlaceHolder"
									required
									suggestedGroups={primaryDecisionMakerPreferredGroup}
									value={primaryDecisionMakerValue}
								/>
							)}
						</FlexRowCenter>
						<FlexRowCenter
							flexGrow={1}
							mt={3}
						>
							<GreyLabelText
								bold
								mr={1}
								res="Message"
							/>
						</FlexRowCenter>
						<FlexRowCenter flexGrow={1}>
							<TextField
								disabled={isStageLockedAndStarted}
								fullWidth
								multiline
								onChange={stage.setStageMessage}
								rows={4}
								value={stage.stageMessage}
							/>
						</FlexRowCenter>
						<FlexRowCenter
							flexGrow={1}
							mt={3}
						>
							<GreyLabelText
								bold
								mr={1}
								res="StageCommentsType"
							/>
						</FlexRowCenter>
						<FlexRowCenter
							flexGrow={1}
							gap={2}
						>
							<StyledSingleSelectForStage
								disableClearable
								disabled={isStageLockedAndStarted}
								onChange={stage.setCommentsType}
								options={store.stageCommentsTypeOptions}
								value={commentsTypeValue}
							/>
						</FlexRowCenter>
					</FlexColumn>
				</AccordionDetails>
			</Accordion>
		</FlexRowCenter>
	);
});